import React from 'react';
import { cn } from '../../utils';

interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string;
  helperText?: string;
}

export const Input = React.forwardRef<HTMLInputElement, InputProps>(({ 
  className, 
  label, 
  error, 
  helperText, 
  id, 
  ...props 
}, ref) => {
  const inputId = id || label?.toLowerCase().replace(/\s+/g, '-');
  const errorId = error ? `${inputId}-error` : undefined;
  const helperTextId = helperText && !error ? `${inputId}-helper` : undefined;

  return (
    <div className="space-y-1">
      {label && (
        <label 
          htmlFor={inputId}
          className="block text-sm font-medium text-secondary-700 dark:text-secondary-200 dark:text-secondary-300"
        >
          {label}
        </label>
      )}
      <input
        ref={ref}
        id={inputId}
        aria-invalid={!!error}
        aria-describedby={errorId || helperTextId}
        className={cn(
          'block w-full rounded-md border border-secondary-300 dark:border-secondary-600 px-3 py-2 text-sm bg-white dark:bg-secondary-800 text-secondary-900 dark:text-white dark:text-secondary-100',
          'placeholder-secondary-400 dark:placeholder-secondary-500 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500',
          'disabled:bg-secondary-50 dark:disabled:bg-secondary-900 disabled:text-secondary-500',
          error && 'border-red-500 focus:border-red-500 focus:ring-red-500',
          className
        )}
        {...props}
      />
      {error && (
        <p id={errorId} role="alert" className="text-sm text-red-600 dark:text-red-400">{error}</p>
      )}
      {helperText && !error && (
        <p id={helperTextId} className="text-sm text-secondary-500 dark:text-secondary-400">{helperText}</p>
      )}
    </div>
  );
});

Input.displayName = 'Input';

interface TextareaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: string;
  error?: string;
  helperText?: string;
}

export const Textarea = React.forwardRef<HTMLTextAreaElement, TextareaProps>(({ 
  className, 
  label, 
  error, 
  helperText, 
  id, 
  ...props 
}, ref) => {
  const textareaId = id || label?.toLowerCase().replace(/\s+/g, '-');
  const errorId = error ? `${textareaId}-error` : undefined;
  const helperTextId = helperText && !error ? `${textareaId}-helper` : undefined;

  return (
    <div className="space-y-1">
      {label && (
        <label 
          htmlFor={textareaId}
          className="block text-sm font-medium text-secondary-700 dark:text-secondary-200 dark:text-secondary-300"
        >
          {label}
        </label>
      )}
      <textarea
        ref={ref}
        id={textareaId}
        aria-invalid={!!error}
        aria-describedby={errorId || helperTextId}
        className={cn(
          'block w-full rounded-md border border-secondary-300 dark:border-secondary-600 px-3 py-2 text-sm bg-white dark:bg-secondary-800 text-secondary-900 dark:text-white dark:text-secondary-100',
          'placeholder-secondary-400 dark:placeholder-secondary-500 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500',
          'disabled:bg-secondary-50 dark:disabled:bg-secondary-900 disabled:text-secondary-500',
          error && 'border-red-500 focus:border-red-500 focus:ring-red-500',
          className
        )}
        {...props}
      />
      {error && (
        <p id={errorId} role="alert" className="text-sm text-red-600 dark:text-red-400">{error}</p>
      )}
      {helperText && !error && (
        <p id={helperTextId} className="text-sm text-secondary-500 dark:text-secondary-400">{helperText}</p>
      )}
    </div>
  );
});

Textarea.displayName = 'Textarea';

interface SelectProps extends React.SelectHTMLAttributes<HTMLSelectElement> {
  label?: string;
  error?: string;
  helperText?: string;
  children: React.ReactNode;
}

export const Select = React.forwardRef<HTMLSelectElement, SelectProps>(({ 
  className, 
  label, 
  error, 
  helperText, 
  id, 
  children,
  ...props 
}, ref) => {
  const selectId = id || label?.toLowerCase().replace(/\s+/g, '-');
  const errorId = error ? `${selectId}-error` : undefined;
  const helperTextId = helperText && !error ? `${selectId}-helper` : undefined;

  return (
    <div className="space-y-1">
      {label && (
        <label 
          htmlFor={selectId}
          className="block text-sm font-medium text-secondary-700 dark:text-secondary-200 dark:text-secondary-300"
        >
          {label}
        </label>
      )}
      <select
        ref={ref}
        id={selectId}
        aria-invalid={!!error}
        aria-describedby={errorId || helperTextId}
        className={cn(
          'block w-full rounded-md border border-secondary-300 dark:border-secondary-600 px-3 py-2 text-sm bg-white dark:bg-secondary-800 text-secondary-900 dark:text-white dark:text-secondary-100',
          'focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500',
          'disabled:bg-secondary-50 dark:disabled:bg-secondary-900 disabled:text-secondary-500',
          error && 'border-red-500 focus:border-red-500 focus:ring-red-500',
          className
        )}
        {...props}
      >
        {children}
      </select>
      {error && (
        <p id={errorId} role="alert" className="text-sm text-red-600 dark:text-red-400">{error}</p>
      )}
      {helperText && !error && (
        <p id={helperTextId} className="text-sm text-secondary-500 dark:text-secondary-400">{helperText}</p>
      )}
    </div>
  );
});

Select.displayName = 'Select';

interface CheckboxProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string;
  helperText?: string;
}

export const Checkbox = React.forwardRef<HTMLInputElement, CheckboxProps>(({ 
  className, 
  label, 
  error, 
  helperText, 
  id, 
  ...props 
}, ref) => {
  const checkboxId = id || label?.toLowerCase().replace(/\s+/g, '-');
  const errorId = error ? `${checkboxId}-error` : undefined;
  const helperTextId = helperText && !error ? `${checkboxId}-helper` : undefined;

  return (
    <div className="space-y-1">
      <div className="flex items-center">
        <input
          ref={ref}
          type="checkbox"
          id={checkboxId}
          aria-invalid={!!error}
          aria-describedby={errorId || helperTextId}
          className={cn(
            'h-4 w-4 rounded border-secondary-300 dark:border-secondary-600 text-primary-600 dark:bg-secondary-800',
            'focus:ring-primary-500 focus:ring-2',
            error && 'border-red-500',
            className
          )}
          {...props}
        />
        {label && (
          <label 
            htmlFor={checkboxId}
            className="ml-2 block text-sm text-secondary-700 dark:text-secondary-200 dark:text-secondary-300"
          >
            {label}
          </label>
        )}
      </div>
      {error && (
        <p id={errorId} role="alert" className="text-sm text-red-600 dark:text-red-400">{error}</p>
      )}
      {helperText && !error && (
        <p id={helperTextId} className="text-sm text-secondary-500 dark:text-secondary-400">{helperText}</p>
      )}
    </div>
  );
});

Checkbox.displayName = 'Checkbox';
